import React, { useMemo, useRef, useState, useCallback } from 'react';

const defaultPrizes = ['৳7','৳14','৳21','৳178','৳28','৳35','৳999','৳9999'];
const defaultRestricted = new Set(['৳178','৳999','৳9999']);

export default function FreeSpin({
  apiBaseUrl,
  token = (typeof window !== 'undefined' ? localStorage.getItem('token') : null),
  logoUrl,
  prizes = defaultPrizes,
  restricted = defaultRestricted,
  onResult,
}) {
  const wheelRef = useRef(null);
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [pendingIndex, setPendingIndex] = useState(null);
  const [wonAmount, setWonAmount] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [errorAlreadyClaimed, setErrorAlreadyClaimed] = useState(false);
  const [eligibilityChecked, setEligibilityChecked] = useState(false);
  const [eligible, setEligible] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const sliceCount = prizes.length;
  const sliceAngle = 360 / sliceCount;

  const allowedIndexes = useMemo(() => (
    prizes
      .map((p, i) => (restricted.has(p) ? -1 : i))
      .filter((i) => i !== -1)
  ), [prizes, restricted]);

  const normalize = (angle) => {
    let a = angle % 360;
    if (a < 0) a += 360;
    return a;
  };

  const centerAngleOf = (index) => index * sliceAngle + sliceAngle / 2;

  const spinToIndex = useCallback((targetIndex) => {
    const current = normalize(rotation);
    const center = centerAngleOf(targetIndex);
    const margin = Math.max(2, Math.min(8, sliceAngle / 6));
    const epsRange = (sliceAngle / 2) - margin;
    const epsilon = (Math.random() * 2 * epsRange) - epsRange;
    const desired = normalize(360 - (center + epsilon));
    let baseDelta = desired - current;
    if (baseDelta < 0) baseDelta += 360;
    const extraTurns = 5 + Math.floor(Math.random() * 3); // 5..7 turns
    const totalDelta = extraTurns * 360 + baseDelta;

    setSpinning(true);
    setPendingIndex(targetIndex);
    setShowResult(false);
    setRotation((prev) => prev + totalDelta);
  }, [rotation, sliceAngle]);

  const spin = useCallback(() => {
    const run = async () => {
      if (spinning || allowedIndexes.length === 0) return;
      // 1. Login check (token exists)
      const remoteId = typeof window !== 'undefined' ? localStorage.getItem('remote_id') : null;
      if (!token) {
        setModalMessage('You must be logged in to spin.');
        setModalOpen(true);
        setEligible(false);
        setEligibilityChecked(true);
        return;
      }
      if (!remoteId) {
        // Without remote_id we cannot fetch user data; treat as not logged in context
        setModalMessage('You must be logged in to spin.');
        setModalOpen(true);
        setEligible(false);
        setEligibilityChecked(true);
        return;
      }

      // Run eligibility checks once per session open
      if (!eligibilityChecked) {
        try {
          // 2. Phone number verification (from server userData)
          const userRes = await fetch(`${apiBaseUrl}/api/user/show?remote_id=${encodeURIComponent(remoteId)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!userRes.ok) {
            setModalMessage('Please verify your phone number first.');
            setModalOpen(true);
            setEligible(false);
            setEligibilityChecked(true);
            return;
          }
          const userData = await userRes.json();
          const isVerified = String(userData?.session_id) === '1';
          if (!isVerified) {
            setModalMessage('Please verify your phone number first.');
            setModalOpen(true);
            setEligible(false);
            setEligibilityChecked(true);
            return;
          }

          // 3. Deposit check (success in last 3 days, from server)
          const txRes = await fetch(`${apiBaseUrl}/api/transaction/show?remote_id=${encodeURIComponent(remoteId)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!txRes.ok) {
            setModalMessage('You must deposit successfully in the last 3 days to spin.');
            setModalOpen(true);
            setEligible(false);
            setEligibilityChecked(true);
            return;
          }
          const txData = await txRes.json();
          const txs = Array.isArray(txData?.transactions) ? txData.transactions : (txData?.data || txData || []);
          const now = new Date();
          const threeDaysAgo = new Date(now);
          threeDaysAgo.setDate(now.getDate() - 3);
          const hasRecentDeposit = txs.some((t) => {
            const created = new Date(t?.created_at);
            return t?.action === 'deposit' && String(t?.status).toLowerCase() === 'success' && created >= threeDaysAgo;
          });
          if (!hasRecentDeposit) {
            setModalMessage('You must deposit successfully in the last 3 days to spin.');
            setModalOpen(true);
            setEligible(false);
            setEligibilityChecked(true);
            return;
          }

          // 4. Daily limit check (from server transactions, heuristic)
          const isSameDay = (d1, d2) => d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
          const today = new Date();
          const alreadyClaimed = txs.some((t) => {
            const created = new Date(t?.created_at);
            const act = String(t?.action || t?.type || '').toLowerCase();
            const looksLikeFreeSpin = act.includes('free') && act.includes('spin');
            return looksLikeFreeSpin && isSameDay(created, today);
          });
          if (alreadyClaimed) {
            setModalMessage('You have already claimed your free spin for today!');
            setModalOpen(true);
            setEligible(false);
            setEligibilityChecked(true);
            return;
          }

          setEligible(true);
          setEligibilityChecked(true);
        } catch (e) {
          setModalMessage('Unable to verify eligibility at the moment. Please try again later.');
          setModalOpen(true);
          setEligible(false);
          setEligibilityChecked(true);
          return;
        }
      }
      if (!eligible) {
        return;
      }
      const targetIndex = allowedIndexes[Math.floor(Math.random() * allowedIndexes.length)];
      spinToIndex(targetIndex);
    };
    run();
  }, [spinning, allowedIndexes, spinToIndex, eligibilityChecked, eligible, apiBaseUrl, token]);

  const onTransitionEnd = async (ev) => {
    if (ev.propertyName !== 'transform' || !spinning) return;
    setSpinning(false);

    let idx = pendingIndex;
    let finalAmount = 0;

    if (idx == null) {
      const finalAngle = normalize(rotation);
      const pointerAngle = normalize(360 - finalAngle);
      idx = Math.floor(pointerAngle / sliceAngle);
      const over = pointerAngle - (idx * sliceAngle);
      if (over > sliceAngle / 2 && idx < sliceCount - 1) idx++;
    }

    const amtLabel = prizes[idx];
    setWonAmount(amtLabel);
    setShowResult(true);
    finalAmount = parseInt(String(amtLabel).replace(/[^\d]/g, ''), 10);

    try {
      const res = await fetch(`${apiBaseUrl}/api/free/spin`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ amount: finalAmount }),
      });

      if (res.status === 400) {
        // Daily limit enforcement from server
        setErrorAlreadyClaimed(true);
        return;
      }
      if (!res.ok) {
        console.error('Free spin failed:', res.status);
        return;
      }
      if (onResult) onResult(finalAmount);
    } catch (err) {
      console.error('Free spin network error:', err);
    }
  };

  return (
    <div className="fs-overlay">
      <div className="fs-modal">
        <div className="fs-wheel-container">
          <div className="fs-pointer" />
          <div
            ref={wheelRef}
            className="fs-wheel"
            style={{ transform: `rotate(${rotation}deg)` }}
            onTransitionEnd={onTransitionEnd}
          >
            {prizes.map((p, i) => (
              <div
                className="fs-slice-number"
                key={i}
                style={{ '--angle': `${i * sliceAngle + sliceAngle / 2}deg` }}
              >
                {p}
              </div>
            ))}
            <div className="fs-center-circle">
              {logoUrl ? (
                <img src={logoUrl} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : null}
            </div>
          </div>
        </div>

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          <button disabled={spinning} onClick={spin} className={`fs-spin-btn ${!spinning ? 'fs-shake' : ''}`}>
            {spinning ? 'Spinning…' : 'SPIN'}
          </button>
        </div>

        {showResult && (
          <div className="fs-result-overlay">
            <div className="fs-result-box">
              🎉 Congratulations!<br />
              You won <span className="fs-amount">{wonAmount}</span><br />
              <button className="fs-close-btn" onClick={() => setShowResult(false)}>OK</button>
            </div>
          </div>
        )}

        {errorAlreadyClaimed && (
          <div className="fs-result-overlay">
            <div className="fs-result-box">
              ❌<br />
              You have already claimed your free spin for today!<br />
              <button className="fs-close-btn" onClick={() => setErrorAlreadyClaimed(false)}>OK</button>
            </div>
          </div>
        )}
        {modalOpen && (
          <div className="fs-result-overlay">
            <div className="fs-result-box">
              ⛔<br />
              {modalMessage}<br />
              <button className="fs-close-btn" onClick={() => setModalOpen(false)}>OK</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
